import { assertSupabaseConfigured, supabase, supabaseConfig } from "../lib/supabase";
import type {
  AppRole,
  CreateInviteInput,
  Invite,
  InviteStatus,
  InviteValidation,
} from "../types/admin";

const INVITE_SELECT = "*, tenants(id,name,slug,status,plan)";

async function parseFunctionResponse(response: Response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: text };
  }
}

function getFunctionErrorMessage(status: number, data: unknown) {
  if (data && typeof data === "object" && "error" in data) {
    return String(data.error);
  }

  if (status === 401 || status === 403) {
    return "Invite activation is blocked by the Edge Function auth settings. Confirm accept-invite is deployed with verify_jwt = false.";
  }

  return `Invite activation failed with status ${status}.`;
}

export const inviteService = {
  getInviteLink(token: string) {
    return `${window.location.origin}/invite/${token}`;
  },

  async validateInvite(token: string): Promise<InviteValidation | null> {
    assertSupabaseConfigured();

    const { data, error } = await supabase.rpc("validate_invite_token", {
      p_token: token,
    });

    if (error) {
      throw error;
    }

    return data?.[0] ?? null;
  },

  async activateNewAccount(input: {
    token: string;
    email: string;
    password: string;
    fullName: string;
  }) {
    assertSupabaseConfigured();

    const response = await fetch(`${supabaseConfig.url}/functions/v1/accept-invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseConfig.anonKey,
      },
      body: JSON.stringify(input),
    });

    const data = await parseFunctionResponse(response);

    if (!response.ok) {
      throw new Error(getFunctionErrorMessage(response.status, data));
    }

    return data;
  },

  async acceptForCurrentUser(token: string) {
    assertSupabaseConfigured();

    const { data, error } = await supabase.rpc("accept_invite_for_current_user", {
      p_token: token,
    });

    if (error) {
      throw error;
    }

    return data?.[0] ?? null;
  },

  async listInvites(filters?: {
    search?: string;
    status?: string;
    role?: string;
    tenantId?: string;
    limit?: number;
  }): Promise<Invite[]> {
    assertSupabaseConfigured();

    let query = supabase
      .from("invites")
      .select(INVITE_SELECT)
      .order("created_at", { ascending: false })
      .limit(filters?.limit ?? 100);

    if (filters?.status && filters.status !== "all") {
      query = query.eq("status", filters.status as InviteStatus);
    }

    if (filters?.role && filters.role !== "all") {
      query = query.eq("role", filters.role as AppRole);
    }

    if (filters?.tenantId && filters.tenantId !== "all") {
      query = query.eq("tenant_id", filters.tenantId);
    }

    if (filters?.search?.trim()) {
      query = query.ilike("invited_email", `%${filters.search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return (data ?? []) as Invite[];
  },

  async createInvite(input: CreateInviteInput) {
    assertSupabaseConfigured();

    if (input.mode === "new_workspace") {
      const { data, error } = await supabase.rpc(
        "admin_create_new_workspace_invite",
        {
          p_invited_email: input.invitedEmail,
          p_invited_name: input.invitedName,
          p_workspace_name: input.workspaceName,
          p_plan: input.plan,
          p_role: "tenant_owner",
          p_expires_at: input.expiresAt ?? null,
          p_trial_days: input.trialDays ?? null,
        },
      );

      if (error) {
        throw new Error(error.message || "Unable to create invite.");
      }

      return data?.[0] ?? null;
    }

    const { data, error } = await supabase.rpc(
      "admin_create_existing_workspace_invite",
      {
        p_tenant_id: input.tenantId,
        p_invited_email: input.invitedEmail,
        p_invited_name: input.invitedName,
        p_role: input.role,
        p_expires_at: input.expiresAt ?? null,
      },
    );

    if (error) {
      throw new Error(error.message || "Unable to create invite.");
    }

    return data?.[0] ?? null;
  },

  async cancelInvite(inviteId: string) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_cancel_invite", {
      p_invite_id: inviteId,
    });

    if (error) {
      throw error;
    }
  },
};
